/**
 * ★ THE 401 RULE FOR EVERY SCREEN'S OWN REQUEST, WRITTEN ONCE.
 *
 * Five clients send this app's screen reads and writes - the cabinet, the editor, the calendar, the
 * application flow and the support pages - and before this file not one of them did anything with a
 * 401 but show it: a token that had merely gone stale put an error state on the screen instead of
 * being refreshed, and a token the platform had refused left the person on a screen that could no
 * longer load anything. Each client now hands its one `fetch` to `sendWithSession` and keeps
 * everything else - its headers, its body, its error types - exactly as it was.
 *
 * THE RULE IS THE CONTRACT'S. Bearer surfaces "send `Bearer realm="moonx"` when no credential was
 * presented and `Bearer realm="moonx", error="invalid_token"` only when a token was presented and
 * failed — the distinction RFC 6750 § 3 draws, and the one that stops a client retrying a token that
 * will never work" (docs/design/07-api/shared.yaml). So:
 *
 *   bare 401            possibly our own staleness: ONE forced refresh, ONE retry. A 401 on the retry
 *                       means the credential is refused, and the session is told.
 *   invalid_token 401   the token will never work: no refresh, no retry, and the session is told.
 *   anything else       the first answer, untouched. A 403, a 409, a 5xx is not about the credential
 *                       and is never retried here - and a 503 is the "cannot verify right now" that
 *                       must never end a session (adr/0010-firebase-integration.md).
 *
 * "THE SESSION IS TOLD" MEANS ONE THING. The refusal reaches `AuthSessionProvider`, which applies it
 * through the reducer's one `applyFailure` (./session-state.ts, event `credential-refused`) - the same
 * place `GET /v1/me`'s own 401s end a session - and the live gate then sends the person to
 * `/sign-in?next=<where they were>` (./gate.ts; `signInPathFor` in ../navigation.ts). This file ends
 * nothing itself; it counts attempts and reports.
 *
 * THE CHALLENGE IS READ BY THE AUTH FEATURE'S OWN PARSER, `tokenWasRejected` (../api/failure.ts),
 * never re-parsed here. `WWW-Authenticate` is not a CORS-safelisted response header, and the API
 * exposes it to this origin on purpose (src/common/http/cors-policy.ts) - without that every
 * 401 would read as bare, and an `invalid_token` would cost one wasted retry before the same end.
 *
 * ★ WRITES ARE REPLAYED, AND THAT IS SAFE BECAUSE A 401 WAS NEVER APPLIED. The retry re-sends the
 * same request, POST, PUT and DELETE included. RFC 9110 § 15.5.2 defines 401 as "the request has not
 * been applied because it lacks valid authentication credentials", and on `authors-api` that is true
 * by construction: `FirebaseIdTokenGuard` is an `APP_GUARD`
 * (src/modules/identity/author/identity-author.module.ts) that runs FIRST and "verifies the bearer
 * token, or 401 / 503" (:17-21), and its no-credential 401 is thrown before anything is read or
 * written (src/modules/identity/author/author-session.service.ts). The 401s a handler can
 * raise itself are "UNREACHABLE WHILE `FirebaseIdTokenGuard` IS AN `APP_GUARD`" and come before any
 * write (src/modules/articles/controllers/articles-cabinet.controller.ts). A refused write
 * did nothing, so sending it once more with a fresh token is sending it for the first time.
 *
 * NO LOOP, BY CONSTRUCTION: at most two sends per call and at most one forced refresh. The budget is
 * per request, as the provider's own single refresh is per `GET /v1/me`.
 *
 * ★ ONE PERSON PER REQUEST. The uid the first token belonged to is read beside it
 * (`currentSessionUid`, ./access-token.ts). If somebody else is signed in by the time the refresh
 * returns, the retry is NOT sent - it would replay one person's request, a write included, under
 * another person's credential - and nothing is reported. The report carries the uid as well, and
 * the provider ignores one that is not about whoever is signed in now.
 */
import { tokenWasRejected } from '../api/failure';
import { currentSessionUid } from './access-token';
import type { SessionTokenSource } from './access-token';
import type { UnauthenticatedFailure } from './session-state';

/** What reaches the session when a credential is refused for good. */
export interface CredentialRefusal {
  /** The uid the refused token belonged to. `null` when the request went out with no session. */
  readonly uid: string | null;
  /**
   * `errorCode` and `problem` are always null. The body is the CLIENT'S to read - it becomes the
   * client's own error type after this returns - and reading it here would consume it. Nothing
   * branches on either: the challenge is what decides (../api/failure.ts, `actionFor`).
   */
  readonly failure: UnauthenticatedFailure;
}

type RefusalListener = (refusal: CredentialRefusal) => void;

/**
 * Who hears a refusal: the mounted `AuthSessionProvider`, and in a spec whatever the spec adds. A
 * module-level set, for the reason `authClient()` is memoised: the clients are plain modules built
 * outside React, and this is the one wire from them to the session.
 */
const listeners = new Set<RefusalListener>();

/** Subscribe to refusals. Returns the unsubscribe. The provider is the one subscriber in the app. */
export function onCredentialRefused(listener: RefusalListener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** Tells every subscriber. The default `report` below; exported so a spec can drive the provider. */
export function reportCredentialRefused(refusal: CredentialRefusal): void {
  for (const listener of [...listeners]) {
    listener(refusal);
  }
}

/** One attempt: the client's own `fetch`, with this token as its bearer - or none, for `null`. */
export type SendAttempt = (token: string | null) => Promise<Response>;

export interface SendWithSessionOptions {
  /** Whose token the source hands out. Defaults to the app client's current session. */
  readonly currentUid?: () => string | null;
  /** Where a refusal goes. Defaults to the provider, through `reportCredentialRefused`. */
  readonly report?: (refusal: CredentialRefusal) => void;
}

function refusalOf(response: Response): UnauthenticatedFailure {
  const challenge = response.headers.get('www-authenticate');
  return {
    kind: 'unauthenticated',
    tokenPresentedAndFailed: tokenWasRejected(
      challenge === null ? {} : { 'www-authenticate': challenge },
    ),
    errorCode: null,
    problem: null,
  };
}

/**
 * Sends one request under the rule above and returns the answer the client should read: the first
 * one, or the retry's. It throws only what `send` or the token source throws - a token the SDK could
 * not produce is "cannot verify right now", and is left to the client exactly as it always was.
 */
export async function sendWithSession(
  tokenSource: SessionTokenSource,
  send: SendAttempt,
  options: SendWithSessionOptions = {},
): Promise<Response> {
  const currentUid = options.currentUid ?? currentSessionUid;
  const report = options.report ?? reportCredentialRefused;

  const uid = currentUid();
  const first = await send(await tokenSource(false));
  if (first.status !== 401) {
    return first;
  }

  const failure = refusalOf(first);
  if (failure.tokenPresentedAndFailed) {
    report({ uid, failure });
    return first;
  }

  const refreshed = await tokenSource(true);
  if (currentUid() !== uid) {
    return first;
  }
  if (refreshed === null) {
    // Nobody to refresh: the retry would be the same bare request, answered the same way.
    report({ uid, failure });
    return first;
  }

  const second = await send(refreshed);
  if (second.status === 401) {
    report({ uid, failure: refusalOf(second) });
  }
  return second;
}
